// Markt-Modus — EVE-Hero im Claude.ai-Style.
//
// Layout:
//   - Keine Chat-History: zentriertes EveHero (Hello-State).
//   - 1+ Messages: Composer FIX am unteren Rand. Chat-History scrollt darüber.
//     Neueste Antwort unten direkt überm Composer, älteres beim Hochscrollen.
//
// Technik: `absolute inset-0` auf MarktPage damit sie GARANTIERT exakt den
// Outlet-Wrapper füllt — der hat dafür `relative` (siehe app-shell.tsx).
// Damit kann der Composer strukturell nicht mehr aus dem Viewport rutschen,
// egal was das umgebende Flex-Layout macht.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { ImagePlus, LineChart, MessageCircle, Sparkles, Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useRunEve } from '@/hooks/use-run-eve';
import {
  selectMessagesFor,
  useEveChatStore,
  type ChatMessage,
} from '@/stores/eve-chat-store';
import { selectChatMode, useEveChatModeStore } from '@/stores/eve-chat-mode-store';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';
import { EveHero } from '@/components/eve/eve-hero';
import {
  Composer,
  IMAGE_DROP_EVENT,
  type ComposerSubmitInput,
  type ImageDropEventDetail,
} from '@/components/eve/composer';
import { QuickActions } from '@/components/eve/quick-actions';
import { useAuth } from '@/hooks/use-auth';
import { MarkdownText } from '@/lib/markdown';
import type { AgentKey } from '@/types/eve-profile';

const MARKT_PAGE_KEY = '/markt';

export function MarktPage() {
  const { send } = useRunEve();
  const messages = useEveChatStore(selectMessagesFor(MARKT_PAGE_KEY));
  const clearChat = useEveChatStore((s) => s.clear);
  const chatMode = useEveChatModeStore(selectChatMode);
  const toggleChatMode = useEveChatModeStore((s) => s.toggle);
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);
  const { profile } = useAuth();

  // Page-Context für EVE setzen — Markt = primary chat-mode, kein spezieller
  // Daten-Context nötig (Markt-Daten sind eh im LIVE_DATA_SNAPSHOT).
  useEffect(() => {
    setPageContext({
      pageKey: MARKT_PAGE_KEY,
      title: 'Markt',
      summary:
        'Hauptseite mit direktem EVE-Chat. Doc kann frei fragen, EVE hat Zugriff auf alle Live-Daten und Memories.',
    });
    return () => clearPageContext();
  }, [setPageContext, clearPageContext]);

  // Scrollbarer Chat-Container (Claude.ai-Style: neueste unten direkt überm
  // Composer). Sticky-Bottom: wenn der User aktiv hochgescrollt hat (z.B. um
  // ältere Nachrichten zu lesen), KEIN Snap-back nach unten bei neuem Chunk.
  // Erst wieder wenn er unten ist (innerhalb 120px Toleranz).
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  // Page-Level Drag & Drop für Bilder. Beim Dragover über die ganze Page erscheint
  // ein Overlay; Drop dispatcht ein `eve:image-drop`-Event, den der Composer-
  // Subtree abfängt und wie einen File-Picker-Pick verarbeitet. Vorteil: kein
  // Prop-Drilling durch EveHero, beide Layouts (empty + chat) sind abgedeckt.
  const [isPageDragging, setIsPageDragging] = useState(false);
  const pageDragDepthRef = useRef(0);

  useEffect(() => {
    const isFileDrag = (e: globalThis.DragEvent) =>
      e.dataTransfer?.types?.includes('Files') ?? false;

    const onDragEnter = (e: globalThis.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      pageDragDepthRef.current += 1;
      setIsPageDragging(true);
    };
    const onDragOver = (e: globalThis.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: globalThis.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      pageDragDepthRef.current = Math.max(0, pageDragDepthRef.current - 1);
      if (pageDragDepthRef.current === 0) setIsPageDragging(false);
    };
    const onDrop = (e: globalThis.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      pageDragDepthRef.current = 0;
      setIsPageDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      const detail: ImageDropEventDetail = { file };
      window.dispatchEvent(new CustomEvent(IMAGE_DROP_EVENT, { detail }));
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  const handleChatScroll = useCallback(() => {
    const el = chatHistoryRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useEffect(() => {
    if (!isAtBottomRef.current) return;
    const el = chatHistoryRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (input: ComposerSubmitInput) => {
    await send({
      agentKey: 'direct',
      userMsg: input.text,
      imageData: input.imageBase64 ?? undefined,
      imageMime: input.imageMime ?? undefined,
    });
  };

  const handleTrigger = async (agentKey: AgentKey, triggerText: string) => {
    await send({ agentKey, userMsg: triggerText });
  };

  // Pre-Filled Frage aus URL (`?ask=...`) automatisch senden. Wird z.B. vom
  // „Frag EVE"-Button auf der Polymarket-Page genutzt. Sofort nach Mount
  // einmal senden und Query-Param aus URL entfernen damit kein Doppel-Send
  // bei Re-Render.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const ask = searchParams.get('ask');
    if (!ask) return;
    void send({ agentKey: 'direct', userMsg: ask });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('ask');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dropOverlay = isPageDragging ? (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/70 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-amber-400/70 bg-amber-500/[0.06] px-10 py-8 shadow-[0_0_60px_rgba(251,191,36,0.25)]">
        <ImagePlus className="size-10 text-amber-300" />
        <div className="text-center">
          <div className="text-base font-medium text-white">Bild ablegen</div>
          <div className="mt-1 text-[12px] text-neutral-400">PNG, JPG oder WebP · max 5 MB</div>
        </div>
      </div>
    </div>
  ) : null;

  if (messages.length === 0) {
    return (
      <>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute right-4 top-4 z-20">
            <ChatModeToggle mode={chatMode} onToggle={toggleChatMode} />
          </div>
          <EveHero onSubmit={handleSubmit} onTrigger={handleTrigger} />
        </div>
        {dropOverlay}
      </>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Macro-Releases jetzt als Header-Popup (Topbar), nicht mehr als Banner */}
      {/* Status-Zeile oben (Anzahl + Modus-Toggle + Verlauf leeren) */}
      <div className="shrink-0 px-4 pt-3">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between text-[12px] text-neutral-500">
          <span>
            Gespräch mit EVE · {messages.length} Nachricht
            {messages.length === 1 ? '' : 'en'}
          </span>
          <div className="flex items-center gap-2">
            <ChatModeToggle mode={chatMode} onToggle={toggleChatMode} />
            <button
              type="button"
              onClick={() => clearChat(MARKT_PAGE_KEY)}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-neutral-500 hover:text-rose-300"
              aria-label="Chat-Verlauf leeren"
            >
              <Trash2 className="size-3" />
              Verlauf leeren
            </button>
          </div>
        </div>
      </div>

      {/* Chat-History scrollt intern. Älteste oben, neueste unten direkt überm
          Composer (Claude.ai-Style). flex-1 + min-h-0 + overflow-y-auto: scrollt
          nur DIESEN Container, nicht den AppShell. */}
      <div
        ref={chatHistoryRef}
        onScroll={handleChatScroll}
        className="scroll-hidden min-h-0 flex-1 overflow-y-auto px-4 py-4"
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
          {messages.map((m) => (
            <ChatBubble
              key={m.id}
              message={m}
              userInitial={profile?.nickname?.[0] ?? 'D'}
            />
          ))}
        </div>
      </div>

      {/* Composer + Quick-Actions FIX UNTEN — shrink-0 + border-top + bg pinnt
          ihn am unteren Rand, da MarktPage `absolute inset-0 flex flex-col` ist. */}
      <div className="shrink-0 border-t border-white/5 bg-neutral-950/30 px-4 py-3 backdrop-blur-sm">
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-3">
          <Composer onSubmit={handleSubmit} />
          <QuickActions onTrigger={handleTrigger} />
        </div>
      </div>
      {dropOverlay}
    </div>
  );
}

// ── Chat-Bubble ──────────────────────────────────────────────────────────────

interface ChatBubbleProps {
  message: ChatMessage;
  userInitial: string;
}

function ChatBubble({ message, userInitial }: ChatBubbleProps) {
  const isUser = message.role === 'user';
  const isError = !!message.error;

  if (isUser) {
    return (
      <div className="flex items-start gap-2.5">
        <div className="grid size-7 shrink-0 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[12px] font-bold text-neutral-950">
          {userInitial.toUpperCase()}
        </div>
        <div className="glass max-w-[80%] rounded-2xl rounded-tl-md px-4 py-2.5">
          <p className="whitespace-pre-wrap text-sm text-neutral-100">{message.text}</p>
          <div className="mt-1 text-[10px] text-neutral-500">{formatTime(message.createdAt)}</div>
        </div>
      </div>
    );
  }

  // Assistant
  const text = stripEveCallDirective(message.text);

  return (
    <div className="flex items-start gap-2.5">
      <img
        src="/eve-satoshi.png"
        alt="EVE"
        className={`size-7 shrink-0 rounded-full object-cover ring-1 ring-amber-500/40 ${
          message.streaming ? 'animate-pulse' : ''
        }`}
      />
      <div
        className={`glass-strong max-w-[80%] rounded-2xl rounded-tl-md px-4 py-2.5 ${
          isError ? 'border-rose-500/30' : 'border-amber-500/20'
        }`}
      >
        {message.text === '' && message.streaming ? (
          <div className="flex items-center gap-2 text-sm text-neutral-400">
            <Sparkles className="size-3 animate-pulse text-amber-400" />
            <span>EVE denkt nach…</span>
          </div>
        ) : (
          <div
            className={`space-y-2 text-sm ${
              isError ? 'text-rose-300' : 'text-neutral-100'
            }`}
          >
            <MarkdownText text={text} />
            {message.streaming ? (
              <span className="ml-0.5 inline-block size-1.5 animate-pulse rounded-full bg-amber-400 align-middle" />
            ) : null}
          </div>
        )}
        <div className="mt-1 flex items-center gap-2 text-[10px] text-neutral-500">
          <span>{formatTime(message.createdAt)}</span>
          {message.agentKey && message.agentKey !== 'direct' ? (
            <>
              <span>·</span>
              <span className="text-amber-300/70">{message.agentKey}</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/** Entfernt den `<!--EVE_CALL:...-->`-Block aus EVEs Antwort (UI-Display only). */
function stripEveCallDirective(text: string): string {
  return text.replace(/<!--EVE_CALL:[\s\S]*?-->\s*$/, '').trim();
}

// ── ChatMode Toggle ──────────────────────────────────────────────────────────
// V1 App.js Z.2086-2111 Toggle-Pendant: 📊 Trading ↔ 💬 Chat.
// `trading` = voller Markt-Brain mit LIVE_DATA, EVE_CALL-Pflicht.
// `private` = Plauder-Brain ohne Markt-Kontext. Bei Trade-Fragen schaltet die
// Runtime intern via TRADING_DIRECTIVE trotzdem auf Markt-Brain zurück.

interface ChatModeToggleProps {
  mode: 'trading' | 'private';
  onToggle: () => void;
}

function ChatModeToggle({ mode, onToggle }: ChatModeToggleProps): ReactElement {
  const isTrading = mode === 'trading';
  return (
    <button
      type="button"
      onClick={onToggle}
      title={
        isTrading
          ? 'EVE Trading-Modus — Marktanalyse, LIVE_DATA, EVE-Call-Pflicht'
          : 'EVE Private-Modus — Plauder-Chat ohne Markt-Kontext'
      }
      aria-label={`Modus wechseln zu ${isTrading ? 'Private Chat' : 'Trading'}`}
      className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide transition-colors ${
        isTrading
          ? 'border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15'
          : 'border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/15'
      }`}
    >
      {isTrading ? (
        <>
          <LineChart className="size-3" />
          Trading
        </>
      ) : (
        <>
          <MessageCircle className="size-3" />
          Private
        </>
      )}
    </button>
  );
}
